import React, { useState, useCallback } from 'react';
import useSWR from 'swr';
import {
 Text,
 Image,
 Flex,
 Button,
 Tr,
 Td,
 TableContainer,
 Table,
 Thead,
 Th,
 Tbody,
 useToast,
 Box,
 SkeletonText,
 Grid,
} from '@chakra-ui/react';
import { CiSearch } from 'react-icons/ci';
import iconEdit from '../../assets/images/icon-edit.svg';
import { Link } from 'react-router-dom';
import baseService from '../../services/base';
import { AttentionModal, SuccessModal } from '../../components/Modals';
import { Pagination } from '../../components/Pagination';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import Loader from '../../components/Feedback/Loader';
import { search, trash } from '../../assets/new-images/icons';
import { fetchData } from '../../services/fetch';
import debounce from 'lodash/debounce';
import { InputText, Select } from '../../components/Input';

const SurveysTemplate = () => {
 const toast = useToast();
 const [page, setPage] = useState(1);
 const [searchTerm, setSearchTerm] = useState('');
 const [currentTemplate, setCurrentTemplate] = useState(null);
 const [successModalIsOpen, setSuccessModalOpen] = useState(false);
 const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
 const [confirmationModalIsLoading, setConfirmationModalIsLoading] = useState(false);

 const { data, isLoading, mutate } = useSWR(
  `/survey-template?name=${searchTerm}&page=${page}&sort_by=application_creation_date&take=10`,
  fetchData
 );

 const handleSearch = useCallback(
  debounce((value) => setSearchTerm(value), 300),
  []
 );

 const handleDeleteTemplate = (templateId) => {
  setCurrentTemplate(templateId);
  setConfirmationModalIsOpen(true);
 };

 const deleteTemplate = async () => {
  try {
   setConfirmationModalIsLoading(true);
   await baseService.delete(`/survey-template/${currentTemplate}`);
   setSuccessModalOpen(true);
   mutate();
  } catch (err) {
   toast({
    title: 'Não foi possível remover o template',
    status: 'error',
    duration: 5000,
    isClosable: true,
   });
  } finally {
   setConfirmationModalIsLoading(false);
   setConfirmationModalIsOpen(false);
   setCurrentTemplate(null);
  }
 };

 const handleCloseConfirmation = () => {
  setConfirmationModalIsOpen(false);
  setCurrentTemplate(null);
 };

 const closeSuccessModal = () => {
  setSuccessModalOpen(false);
  setCurrentTemplate(null);
 };

 const renderTableBody = () => {
  if (isLoading) return null;

  if (!data?.data?.length) {
   return (
    <Tr>
     <Td colSpan={5} textAlign='center'>
      Nenhum template encontrado.
     </Td>
    </Tr>
   );
  }

  return data?.data?.map((research, index) => (
   <Tr
    key={index}
    height='34px'
    borderBlock='1px solid rgba(0,0,0,0)'
    maxH='46px'
    color='#1e1f21'
    fill='#6d6e6f'
   >
    <Td width='12%' maxW='12%' pr='10px' overflow='hidden !important'>
     {research?.code || '--'}
    </Td>
    <Td>{research.name}</Td>
    <Td textAlign='center'>{research.meta?.totalQuestions}</Td>
    <Td textAlign='center'>
     <Link to={`/templates/${research?.id_survey_template}`}>
      <Image src={iconEdit} alt='edit' height='18px' cursor='pointer' />
     </Link>
    </Td>
    <Td textAlign='center'>
     <Image
      src={trash}
      w='20px'
      h='20px'
      alt='Delete'
      cursor='pointer'
      onClick={() => handleDeleteTemplate(research.id_survey_template)}
     />
    </Td>
   </Tr>
  ));
 };

 return (
  <MainContent minH='80vh'>
   <PageHeaderStructure
    title='Templates'
    subtitle='Cadastre templates de perguntas e tenha facilidade na criação dos questionários.'
   >
    <Button as={Link} to={`/templates/create`} variant='primary' w='215px' h='59px'>
     Cadastrar Template
    </Button>
   </PageHeaderStructure>

   <Grid
    templateColumns='367px 1fr'
    alignItems='center'
    gap={4}
    bg='white'
    mt='32px'
    h='76px'
    p='14px 24px'
    borderRadius='12px 12px 0 0'
   >
    <Select
     name='template-search'
     placeholder='Ordenar por: ordem de criação'
     fontSize='sm'
     bg='#F5F5FB'
     minW='367px'
     maxW='367px'
    />
    <InputText
     value={searchTerm}
     onChange={(e) => setSearchTerm(e.target.value)}
     placeholder='Pesquisar por template'
     autoComplete='off'
     height='52px'
     iconLeft={<CiSearch />}
    />
   </Grid>

   <Box pos='relative'>
    {isLoading && (
     <Box padding='6' boxShadow='lg' bg='white' pos='absolute' w='100%'>
      <SkeletonText mt='4' noOfLines={10} spacing='10' />
     </Box>
    )}

    <TableContainer>
     <Table variant='simple' bg='white'>
      <Thead h='41px' background='rgba(0, 0, 0, 0.1)'>
       <Tr borderColor='#F4F4F4'>
        <Th width='12%' maxW='12%' fontWeight={500} fontSize='13px' color='#16262E'>
         Código do template
        </Th>
        <Th fontWeight={500} fontSize='13px' color='#16262E' width='58%' maxW='58%'>
         Nome do template
        </Th>

        <Th
         fontSize='13px'
         color='#16262E'
         fontWeight={500}
         textAlign='center'
         w='10%'
         maxW='10%'
        >
         Qntd de questões
        </Th>

        <Th fontSize='13px' color='#16262E' fontWeight={500} textAlign='center' w='8%'>
         Editar
        </Th>
        <Th fontSize='13px' color='#16262E' fontWeight={500} textAlign='center' w='8%'>
         Excluir
        </Th>
       </Tr>
      </Thead>
      {!isLoading && (
       <Tbody>
        {data?.data?.map((research, index) => (
         <Tr
          key={index}
          borderBlock='1px solid rgba(0,0,0,0)'
          height='34px'
          maxH='46px'
          color='#1e1f21'
          fill='#6d6e6f'
         >
          <Td width='12%' maxW='12%' pr='10px' overflow='hidden !important'>
           <Text as='span' fontSize='13px' whiteSpace='nowrap' fontWeight={500}>
            {research?.code || '--'}
           </Text>
          </Td>

          <Td
           width='58%'
           maxW='58%'
           pr='10px'
           overflow='hidden'
           whiteSpace='nowrap'
           textOverflow='ellipsis'
          >
           <Text as='span' fontSize='13px' whiteSpace='nowrap' fontWeight={500}>
            {research.name}
           </Text>
          </Td>

          <Td w='10%' maxW='10%' fontSize='13px' fontWeight={500} textAlign={'center'}>
           {research.meta?.totalQuestions}
          </Td>

          <Td width='8%' textAlign={'center'}>
           <Flex align='center' justify='center'>
            <Link to={`/templates/${research?.id_survey_template}`}>
             <Image src={iconEdit} alt='edit' height='18px' cursor='pointer' />
            </Link>
           </Flex>
          </Td>

          <Td width='8%' textAlign={'center'}>
           <Flex align='center' justify='center'>
            <Image
             src={trash}
             w='20px'
             h='20px'
             alt='Delete'
             cursor='pointer'
             transition='box-shadow 100ms ease-in-out'
             onClick={() => handleDeleteTemplate(research.id_survey_template)}
            />
           </Flex>
          </Td>
         </Tr>
        ))}
       </Tbody>
      )}
     </Table>
    </TableContainer>

    {!isLoading && data?.data?.length > 0 && (
     <Pagination
      currentPage={page}
      onPageChange={setPage}
      meta={data?.meta}
      viewing={data?.data?.length}
     />
    )}
   </Box>

   {currentTemplate && (
    <AttentionModal
     isOpen={confirmationModalIsOpen}
     onClose={handleCloseConfirmation}
     title='Deseja excluir o template selecionado?'
     isLoading={confirmationModalIsLoading}
     submit={deleteTemplate}
    />
   )}

   <SuccessModal
    isOpen={successModalIsOpen}
    onClose={closeSuccessModal}
    title='Template excluído com sucesso'
   />
  </MainContent>
 );
};

export default SurveysTemplate;
